"use strict";
/**
 * 活动包错误码定义工具
 *
 * 提供统一的错误码定义模式，确保所有活动包遵循相同的结构规范
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.defineActivityErrorCodes = defineActivityErrorCodes;
exports.generateErrorGroupFromApiPrefix = generateErrorGroupFromApiPrefix;
/**
 * 定义活动包错误码
 *
 * 统一封装错误码定义逻辑，确保所有活动包遵循相同的结构规范
 *
 * @param codes 错误码枚举对象（键值对，键为错误名称，值为错误码数字）
 * @param options 配置选项
 * @returns 错误码定义结果，包含枚举、分组名称和映射表
 *
 * @example
 * ```typescript
 * // shared/src/error-codes.ts
 * import { defineActivityErrorCodes } from '@ark/activity-core';
 *
 * export const HelloWorldResultCode = defineActivityErrorCodes({
 *   ACTIVITY_NOT_STARTED: 31001,
 *   ACTIVITY_ENDED: 31002,
 *   USER_NOT_REGISTERED: 31003,
 * });
 *
 * // 导出枚举（前端使用）
 * export const HelloWorldResultCodeEnum = HelloWorldResultCodeDefinition.enum;
 *
 * // 导出分组名称（后端注册使用）
 * export const HELLO_WORLD_ERROR_GROUP = HelloWorldResultCodeDefinition.group;
 *
 * // 导出映射表（后端注册使用）
 * export const HelloWorldResultCodeMap = HelloWorldResultCodeDefinition.map;
 * ```
 *
 * @example
 * ```typescript
 * // 自定义分组名称
 * export const MyActivityResultCode = defineActivityErrorCodes({
 *   ERROR_1: 32001,
 *   ERROR_2: 32002,
 * }, {
 *   groupName: 'MY_ACTIVITY',
 * });
 * ```
 */
function defineActivityErrorCodes(codes, options = {}) {
    // 生成分组名称
    const group = options.groupName || generateGroupNameFromCodes(codes);
    return {
        enum: codes,
        group,
        map: codes,
    };
}
/**
 * 从错误码定义自动生成分组名称
 * 使用默认命名规则：ACTIVITY_ERROR_CODES
 *
 * 注意：建议显式指定 groupName，使用 activityConfig.apiPrefix 生成
 */
function generateGroupNameFromCodes(codes) {
    // 默认分组名称
    return 'ACTIVITY_ERROR_CODES';
}
/**
 * 从 activityConfig.apiPrefix 生成错误码分组名称
 *
 * 转换规则：
 * - 转换为大写
 * - 将连字符 (-) 替换为下划线 (_)
 *
 * @param apiPrefix API 前缀，如 'hello-world-2024'
 * @returns 分组名称，如 'HELLO_WORLD_2024'
 *
 * @example
 * ```typescript
 * import { generateErrorGroupFromApiPrefix } from '@ark/activity-core';
 * import { activityConfig } from './activity.config';
 *
 * export const MyActivityResultCode = defineActivityErrorCodes({
 *   ERROR_1: 32001,
 * }, {
 *   groupName: generateErrorGroupFromApiPrefix(activityConfig.apiPrefix),
 * });
 * ```
 */
function generateErrorGroupFromApiPrefix(apiPrefix) {
    return apiPrefix.toUpperCase().replace(/-/g, '_');
}
